import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WhatsAppCampaign,
  CreateCampaignData,
  UpdateCampaignData,
} from '@/types/whatsapp';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export function useWhatsAppCampaigns(filters?: { status?: string }) {
  return useQuery<WhatsAppCampaign[]>({
    queryKey: ['whatsapp', 'campaigns', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);

      const res = await fetch(
        `${API_BASE_URL}/whatsapp/campaigns${params.toString() ? `?${params}` : ''}`,
        {
          headers: getAuthHeaders(),
        }
      );
      if (!res.ok) {
        throw new Error('Failed to fetch campaigns');
      }
      const data = await res.json();
      return data.data || data;
    },
  });
}

export function useWhatsAppCampaign(id: string) {
  return useQuery<WhatsAppCampaign>({
    queryKey: ['whatsapp', 'campaigns', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to fetch campaign');
      }
      const data = await res.json();
      return data.data || data;
    },
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCampaignData) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create campaign');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCampaignData }) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update campaign');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns', variables.id] });
    },
  });
}

export function useStartCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to start campaign');
      }
      return res.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns', id] });
    },
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}/pause`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to pause campaign');
      }
      return res.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns', id] });
    },
  });
}

export function useCancelCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to cancel campaign');
      }
      return res.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns', id] });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/campaigns/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to delete campaign');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'campaigns'] });
    },
  });
}
